import React, { useEffect, useMemo, useRef, useState } from "react";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import QBankReferenceEditor from "./QBankReferenceEditor";
import {
    normalizeReferencesForEditor,
    QBankReference,
    QBankReferenceDraft,
    serializeReferencesForSave,
} from "../utils/qbankReferences";
import { QBankCode } from "../utils/qbankProfile";
import styles from "./QBankQuestionEntryModal.module.css";

export interface QBankEntryCollection {
    id: string;
    title: string;
}

export interface QBankEntryResult {
    created_count: number;
    completed_count: number;
    queued_count: number;
    embedded_count?: number;
    question_ids: string[];
    upload_id?: string | null;
    collection_id: string;
}

interface QBankQuestionEntryModalProps {
    userId: string;
    bankCode: QBankCode;
    specialties: string[];
    defaultSpecialty: string;
    optionCount: number | null;
    collections: QBankEntryCollection[];
    initialCollectionId?: string;
    lockCollection?: boolean;
    onClose: () => void;
    onImported: (result: QBankEntryResult) => void | Promise<void>;
}

type EntryTab = "manual" | "csv" | "json";

interface EntryOption {
    label: string;
    text: string;
}

interface QuestionEmbedding {
    model: string;
    dimensions: number;
    content_hash: string;
    vector: number[];
}

interface EntryQuestion {
    question_text: string;
    options: EntryOption[];
    specialty: string;
    difficulty: string;
    correct_answer: string;
    explanation: string;
    question_references: QBankReference[];
    embedding?: QuestionEmbedding | string | null;
}

interface PreviewRow {
    row_number: number;
    question: EntryQuestion;
    errors: string[];
    warnings?: string[];
}

interface ImportPreview {
    collection: QBankEntryCollection;
    source_format: "csv" | "json";
    filename: string;
    rows: PreviewRow[];
    total_count: number;
    valid_count: number;
    invalid_count: number;
    can_import: boolean;
}

// Preserve malformed input for a useful server-side row error rather than dropping it.
const parseEmbeddingInput = (value: EntryQuestion["embedding"]): EntryQuestion["embedding"] => {
    if (typeof value !== "string") return value;
    if (!value.trim()) return null;
    try { return JSON.parse(value); } catch { return value; }
};

const emptyQuestion = (
    defaultSpecialty: string,
    optionCount: number | null
): EntryQuestion => ({
    question_text: "",
    options: Array.from(
        { length: optionCount || 4 },
        (_, index) => ({ label: String.fromCharCode(65 + index), text: "" })
    ),
    specialty: defaultSpecialty,
    difficulty: "medium",
    correct_answer: "",
    explanation: "",
    question_references: [],
});

const exampleQuestion = (bankCode: QBankCode, specialty: string) => bankCode === "smle"
    ? {
        question_text: "Which finding most strongly supports acute pericarditis?",
        options: [
            { label: "A", text: "Diffuse ST-segment elevation" },
            { label: "B", text: "Localized ST depression" },
            { label: "C", text: "Pathological Q waves" },
            { label: "D", text: "Prolonged PR interval alone" },
        ],
        specialty,
        difficulty: "medium",
        correct_answer: "",
        explanation: "",
        question_references: [],
    }
    : {
        question_text: "Which material is commonly used for direct pulp capping?",
        options: [
            { label: "A", text: "Zinc phosphate cement" },
            { label: "B", text: "Mineral trioxide aggregate" },
            { label: "C", text: "Composite resin" },
            { label: "D", text: "Zinc oxide eugenol" },
        ],
        specialty,
        difficulty: "medium",
        correct_answer: "",
        explanation: "",
        question_references: [],
    };

const relabelOptions = (options: EntryOption[]) => options.map((option, index) => ({
    ...option,
    label: String.fromCharCode(65 + index),
}));

const QBankQuestionEntryModal: React.FC<QBankQuestionEntryModalProps> = ({
    userId,
    bankCode,
    specialties,
    defaultSpecialty,
    optionCount,
    collections,
    initialCollectionId,
    lockCollection = false,
    onClose,
    onImported,
}) => {
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [activeTab, setActiveTab] = useState<EntryTab>("manual");
    const [collectionId, setCollectionId] = useState(
        initialCollectionId || (collections.length === 1 ? collections[0].id : "")
    );
    const resolvedDefaultSpecialty = specialties.includes(defaultSpecialty)
        ? defaultSpecialty
        : specialties[0] || "";
    const [manualQuestion, setManualQuestion] = useState<EntryQuestion>(() => (
        emptyQuestion(resolvedDefaultSpecialty, optionCount)
    ));
    const [manualReferenceDrafts, setManualReferenceDrafts] = useState<QBankReferenceDraft[]>([]);
    const [manualImage, setManualImage] = useState<File | null>(null);
    const [manualImageAlt, setManualImageAlt] = useState("");
    const [manualErrors, setManualErrors] = useState<string[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<ImportPreview | null>(null);
    const [previewReferenceDrafts, setPreviewReferenceDrafts] = useState<Record<number, QBankReferenceDraft[]>>({});
    const [expandedPreviewRows, setExpandedPreviewRows] = useState<Set<number>>(new Set());
    const [busy, setBusy] = useState(false);
    const [topError, setTopError] = useState("");
    const [result, setResult] = useState<QBankEntryResult | null>(null);
    const manualImagePreview = useMemo(
        () => manualImage ? URL.createObjectURL(manualImage) : "",
        [manualImage]
    );
    const jsonExample = useMemo(() => JSON.stringify({
        questions: [exampleQuestion(bankCode, resolvedDefaultSpecialty)],
    }, null, 2), [bankCode, resolvedDefaultSpecialty]);
    const csvTemplate = useMemo(() => {
        const example = exampleQuestion(bankCode, resolvedDefaultSpecialty);
        const optionValues = "ABCDEF".split("").map((label) => (
            example.options.find((option) => option.label === label)?.text || ""
        ));
        const escaped = [
            example.question_text,
            ...optionValues,
            example.specialty,
            example.difficulty,
            "",
            "",
            "[]",
        ].map((value) => `"${String(value).replace(/"/g, '""')}"`);
        return [
            "question_text,option_a,option_b,option_c,option_d,option_e,option_f,specialty,difficulty,correct_answer,explanation,references_json",
            escaped.join(","),
        ].join("\n");
    }, [bankCode, resolvedDefaultSpecialty]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !busy) onClose();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [busy, onClose]);

    useEffect(() => () => {
        if (manualImagePreview) URL.revokeObjectURL(manualImagePreview);
    }, [manualImagePreview]);

    const selectedCollection = useMemo(
        () => collections.find((collection) => collection.id === collectionId),
        [collectionId, collections]
    );

    const switchTab = (tab: EntryTab) => {
        if (busy) return;
        setActiveTab(tab);
        setPreview(null);
        setPreviewReferenceDrafts({});
        setExpandedPreviewRows(new Set());
        setSelectedFile(null);
        setTopError("");
        setManualErrors([]);
        setResult(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const updateManualOption = (index: number, text: string) => {
        setManualQuestion((current) => ({
            ...current,
            options: current.options.map((option, optionIndex) => (
                optionIndex === index ? { ...option, text } : option
            )),
        }));
        setManualErrors([]);
    };

    const addManualOption = () => {
        setManualQuestion((current) => {
            if (current.options.length >= 6) return current;
            return {
                ...current,
                options: [...current.options, {
                    label: String.fromCharCode(65 + current.options.length),
                    text: "",
                }],
            };
        });
    };

    const removeManualOption = (index: number) => {
        setManualQuestion((current) => {
            if (current.options.length <= 2) return current;
            const removedLabel = current.options[index]?.label;
            const originalCorrectIndex = current.options.findIndex(
                (option) => option.label === current.correct_answer
            );
            const options = relabelOptions(current.options.filter((_, optionIndex) => optionIndex !== index));
            let correctAnswer = current.correct_answer;
            if (removedLabel === current.correct_answer) {
                correctAnswer = "";
            } else if (originalCorrectIndex > index) {
                correctAnswer = String.fromCharCode(64 + originalCorrectIndex);
            }
            return { ...current, options, correct_answer: correctAnswer };
        });
    };

    const questionForRequest = (
        question: EntryQuestion,
        referenceDrafts?: QBankReferenceDraft[]
    ): EntryQuestion => ({
        ...question,
        embedding: parseEmbeddingInput(question.embedding),
        question_references: referenceDrafts
            ? serializeReferencesForSave(referenceDrafts)
            : question.question_references || [],
    });

    const submitQuestions = async (
        sourceFormat: EntryTab,
        filename: string | undefined,
        questions: EntryQuestion[],
        image?: File | null,
        imageAltText?: string
    ) => {
        if (!collectionId) {
            setTopError("Select a target collection.");
            return;
        }
        setBusy(true);
        setTopError("");
        try {
            const requestPayload = {
                    user_id: userId,
                    bank_code: bankCode,
                    collection_id: collectionId,
                    source_format: sourceFormat,
                    filename,
                    questions,
            };
            const requestInit: RequestInit = image
                ? (() => {
                    const formData = new FormData();
                    formData.append("payload", JSON.stringify(requestPayload));
                    formData.append("image", image);
                    formData.append("image_alt_text", imageAltText || "");
                    return { method: "POST", body: formData };
                })()
                : {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(requestPayload),
                };
            const response = await authenticatedFetch(`${apiUrl}/api/admin/qbank/questions`, requestInit);
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                if (response.status === 422 && Array.isArray(payload.rows)) {
                    if (sourceFormat === "manual") {
                        setManualErrors(payload.rows[0]?.errors || [payload.error]);
                    } else {
                        setPreview(payload as ImportPreview);
                        setPreviewReferenceDrafts(Object.fromEntries(
                            (payload.rows as PreviewRow[]).map((row) => [
                                row.row_number,
                                normalizeReferencesForEditor(row.question.question_references),
                            ])
                        ));
                        setExpandedPreviewRows(new Set(
                            (payload.rows as PreviewRow[])
                                .filter((row) => row.errors.length > 0)
                                .map((row) => row.row_number)
                        ));
                    }
                }
                throw new Error(payload.error || "Questions could not be imported.");
            }
            const importResult = payload as QBankEntryResult;
            setResult(importResult);
            await onImported(importResult);
        } catch (error: any) {
            setTopError(error.message || "Questions could not be imported.");
        } finally {
            setBusy(false);
        }
    };

    const submitManual = () => submitQuestions(
        "manual",
        undefined,
        [questionForRequest(manualQuestion, manualReferenceDrafts)],
        manualImage,
        manualImageAlt
    );

    const previewFile = async () => {
        if (!collectionId || !selectedFile) {
            setTopError(!collectionId ? "Select a target collection." : "Select a CSV or JSON file.");
            return;
        }
        setBusy(true);
        setTopError("");
        setPreview(null);
        try {
            const formData = new FormData();
            formData.append("user_id", userId);
            formData.append("bank_code", bankCode);
            formData.append("collection_id", collectionId);
            formData.append("file", selectedFile);
            const response = await authenticatedFetch(
                `${apiUrl}/api/admin/qbank/questions/import/preview`,
                { method: "POST", body: formData }
            );
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(payload.error || "The file could not be previewed.");
            setPreview(payload as ImportPreview);
            setPreviewReferenceDrafts(Object.fromEntries(
                (payload.rows as PreviewRow[]).map((row) => [
                    row.row_number,
                    normalizeReferencesForEditor(row.question.question_references),
                ])
            ));
            setExpandedPreviewRows(new Set(
                (payload.rows as PreviewRow[])
                    .filter((row) => row.errors.length > 0)
                    .map((row) => row.row_number)
            ));
        } catch (error: any) {
            setTopError(error.message || "The file could not be previewed.");
        } finally {
            setBusy(false);
        }
    };

    const updatePreviewQuestion = (index: number, updates: Partial<EntryQuestion>) => {
        setPreview((current) => {
            if (!current) return current;
            const rows = current.rows.map((row, rowIndex) => (
                rowIndex === index
                    ? { ...row, question: { ...row.question, ...updates }, errors: [], warnings: [] }
                    : row
            ));
            const invalidCount = rows.filter((row) => row.errors.length > 0).length;
            return {
                ...current,
                rows,
                invalid_count: invalidCount,
                valid_count: rows.length - invalidCount,
                can_import: rows.length > 0 && invalidCount === 0,
            };
        });
    };

    const updatePreviewOption = (rowIndex: number, optionIndex: number, text: string) => {
        if (!preview) return;
        const question = preview.rows[rowIndex].question;
        updatePreviewQuestion(rowIndex, {
            options: question.options.map((option, index) => (
                index === optionIndex ? { ...option, text } : option
            )),
        });
    };

    const addPreviewOption = (rowIndex: number) => {
        if (!preview) return;
        const question = preview.rows[rowIndex].question;
        if (question.options.length >= 6) return;
        updatePreviewQuestion(rowIndex, {
            options: [...question.options, {
                label: String.fromCharCode(65 + question.options.length),
                text: "",
            }],
        });
    };

    const removePreviewOption = (rowIndex: number, optionIndex: number) => {
        if (!preview) return;
        const question = preview.rows[rowIndex].question;
        if (question.options.length <= 2) return;
        const removedLabel = question.options[optionIndex]?.label;
        const options = relabelOptions(question.options.filter((_, index) => index !== optionIndex));
        const correctIndex = question.options.findIndex((option) => option.label === question.correct_answer);
        const correctAnswer = removedLabel === question.correct_answer
            ? ""
            : correctIndex > optionIndex
                ? String.fromCharCode(64 + correctIndex)
                : question.correct_answer;
        updatePreviewQuestion(rowIndex, { options, correct_answer: correctAnswer });
    };

    const removePreviewRow = (index: number) => {
        const removedRowNumber = preview?.rows[index]?.row_number;
        setPreview((current) => {
            if (!current) return current;
            const rows = current.rows.filter((_, rowIndex) => rowIndex !== index);
            const invalidCount = rows.filter((row) => row.errors.length > 0).length;
            return {
                ...current,
                rows,
                total_count: rows.length,
                valid_count: rows.length - invalidCount,
                invalid_count: invalidCount,
                can_import: rows.length > 0 && invalidCount === 0,
            };
        });
        if (removedRowNumber !== undefined) {
            setPreviewReferenceDrafts((current) => {
                const next = { ...current };
                delete next[removedRowNumber];
                return next;
            });
            setExpandedPreviewRows((current) => {
                const next = new Set(current);
                next.delete(removedRowNumber);
                return next;
            });
        }
    };

    const submitPreview = () => {
        if (!preview) return;
        submitQuestions(
            preview.source_format,
            preview.filename,
            preview.rows.map((row) => row.question)
        );
    };

    const downloadCsvTemplate = () => {
        const blob = new Blob([csvTemplate], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = "qbank_import_template.csv";
        anchor.click();
        URL.revokeObjectURL(url);
    };

    const copyJsonExample = async () => {
        try {
            await navigator.clipboard.writeText(jsonExample);
            (window as any).showNotification?.("JSON example copied", "success");
        } catch {
            setTopError("The JSON example could not be copied.");
        }
    };

    return (
        <div className={styles.overlay} onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busy) onClose();
        }}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="qbank-entry-title">
                <div className={styles.header}>
                    <div>
                        <h2 id="qbank-entry-title">Add Questions</h2>
                        <p>Create one question or import a reviewed structured batch.</p>
                    </div>
                    <button className={styles.closeButton} onClick={onClose} disabled={busy} aria-label="Close">×</button>
                </div>

                {result ? (
                    <div className={styles.successState}>
                        <div className={styles.successIcon}>✓</div>
                        <h3>{result.created_count} question{result.created_count === 1 ? "" : "s"} added</h3>
                        <p>
                            {result.completed_count} ready for review and publication · {result.queued_count} queued for Agent6
                        </p>
                        {!!result.embedded_count && <p>{result.embedded_count} precomputed embedding{result.embedded_count === 1 ? "" : "s"} saved</p>}
                        <div className={styles.footerActions}>
                            <button className={styles.secondaryButton} onClick={() => {
                                setResult(null);
                                setPreview(null);
                                setPreviewReferenceDrafts({});
                                setExpandedPreviewRows(new Set());
                                setSelectedFile(null);
                                setManualQuestion(emptyQuestion(resolvedDefaultSpecialty, optionCount));
                                setManualReferenceDrafts([]);
                                setManualImage(null);
                                setManualImageAlt("");
                                setManualErrors([]);
                                setTopError("");
                            }}>Add more</button>
                            <button className={styles.primaryButton} onClick={onClose}>Done</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className={styles.collectionRow}>
                            <label htmlFor="qbank-entry-collection">Target collection</label>
                            {lockCollection ? (
                                <strong>{selectedCollection?.title || "Collection"}</strong>
                            ) : (
                                <select
                                    id="qbank-entry-collection"
                                    value={collectionId}
                                    onChange={(event) => {
                                        setCollectionId(event.target.value);
                                        setPreview(null);
                                        setPreviewReferenceDrafts({});
                                        setExpandedPreviewRows(new Set());
                                        setTopError("");
                                    }}
                                    disabled={busy}
                                >
                                    <option value="">Select a collection</option>
                                    {collections.map((collection) => (
                                        <option key={collection.id} value={collection.id}>{collection.title}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className={styles.tabs} role="tablist" aria-label="Question entry method">
                            {(["manual", "csv", "json"] as EntryTab[]).map((tab) => (
                                <button
                                    key={tab}
                                    role="tab"
                                    aria-selected={activeTab === tab}
                                    className={activeTab === tab ? styles.activeTab : ""}
                                    onClick={() => switchTab(tab)}
                                    disabled={busy}
                                >
                                    {tab === "manual" ? "Manual" : tab.toUpperCase()}
                                </button>
                            ))}
                        </div>

                        {topError && <div className={styles.topError} role="alert">{topError}</div>}

                        {activeTab === "manual" ? (
                            <div className={styles.content}>
                                {manualErrors.length > 0 && (
                                    <ul className={styles.errorList}>
                                        {manualErrors.map((error) => <li key={error}>{error}</li>)}
                                    </ul>
                                )}
                                <QuestionEditor
                                    editorId="manual"
                                    question={manualQuestion}
                                    referenceDrafts={manualReferenceDrafts}
                                    onQuestionChange={(updates) => {
                                        setManualQuestion((current) => ({ ...current, ...updates }));
                                        setManualErrors([]);
                                    }}
                                    onReferencesChange={setManualReferenceDrafts}
                                    onOptionChange={updateManualOption}
                                    onAddOption={addManualOption}
                                    onRemoveOption={removeManualOption}
                                    specialties={specialties}
                                    fixedOptionCount={optionCount}
                                />
                                <div className={styles.imageEditor}>
                                    <div className={styles.fieldHeader}>
                                        <span>Question image (optional)</span>
                                        <small>JPEG, PNG, or WebP; maximum 10 MB. Image questions require an answer and explanation.</small>
                                    </div>
                                    {manualImagePreview && (
                                        <img className={styles.imagePreview} src={manualImagePreview} alt="Selected question preview" />
                                    )}
                                    <input
                                        type="file"
                                        aria-label="Question image file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={(event) => {
                                            setManualImage(event.target.files?.[0] || null);
                                            setManualErrors([]);
                                        }}
                                        disabled={busy}
                                    />
                                    {manualImage && (
                                        <>
                                            <label className={styles.fullWidth}>
                                                <span>Image description</span>
                                                <input
                                                    type="text"
                                                    value={manualImageAlt}
                                                    maxLength={300}
                                                    onChange={(event) => setManualImageAlt(event.target.value)}
                                                    placeholder="Objectively describe the image without revealing the answer"
                                                />
                                            </label>
                                            <button
                                                type="button"
                                                className={styles.removeQuestionButton}
                                                onClick={() => {
                                                    setManualImage(null);
                                                    setManualImageAlt("");
                                                }}
                                            >
                                                Remove image
                                            </button>
                                        </>
                                    )}
                                </div>
                                <div className={styles.footerActions}>
                                    <button className={styles.secondaryButton} onClick={onClose} disabled={busy}>Cancel</button>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={submitManual}
                                        disabled={busy || !collectionId || Boolean(
                                            manualImage && (
                                                !manualImageAlt.trim()
                                                || !manualQuestion.correct_answer
                                                || !manualQuestion.explanation.trim()
                                            )
                                        )}
                                    >
                                        {busy ? "Adding..." : "Add Question"}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.content}>
                                <div className={styles.fileTools}>
                                    <div>
                                        <h3>{activeTab.toUpperCase()} import</h3>
                                        <p>Up to 500 questions in one UTF-8 file (maximum 2 MB).</p>
                                    </div>
                                    {activeTab === "csv" ? (
                                        <button className={styles.linkButton} onClick={downloadCsvTemplate}>Download CSV template</button>
                                    ) : (
                                        <button className={styles.linkButton} onClick={copyJsonExample}>Copy JSON example</button>
                                    )}
                                </div>
                                <div className={styles.filePicker}>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        aria-label={`${activeTab.toUpperCase()} file`}
                                        accept={activeTab === "csv" ? ".csv,text/csv" : ".json,application/json"}
                                        onChange={(event) => {
                                            setSelectedFile(event.target.files?.[0] || null);
                                            setPreview(null);
                                            setPreviewReferenceDrafts({});
                                            setExpandedPreviewRows(new Set());
                                            setTopError("");
                                        }}
                                        disabled={busy}
                                    />
                                    <button
                                        className={styles.primaryButton}
                                        onClick={previewFile}
                                        disabled={busy || !collectionId || !selectedFile}
                                    >
                                        {busy ? "Validating..." : "Preview file"}
                                    </button>
                                </div>

                                {preview && (
                                    <div className={styles.previewSection}>
                                        <div className={styles.previewSummary}>
                                            <strong>{preview.rows.length} question{preview.rows.length === 1 ? "" : "s"}</strong>
                                            <span>{preview.rows.filter((row) => row.errors.length > 0).length} need attention</span>
                                        </div>
                                        <div className={styles.previewList}>
                                            {preview.rows.map((row, rowIndex) => (
                                                <div className={styles.previewCard} key={`${row.row_number}-${rowIndex}`}>
                                                    <button
                                                        type="button"
                                                        className={styles.previewSummaryButton}
                                                        aria-expanded={expandedPreviewRows.has(row.row_number)}
                                                        onClick={() => setExpandedPreviewRows((current) => {
                                                            const next = new Set(current);
                                                            if (next.has(row.row_number)) next.delete(row.row_number);
                                                            else next.add(row.row_number);
                                                            return next;
                                                        })}
                                                    >
                                                        <span>Row {row.row_number}: {row.question.question_text || "Untitled question"}</span>
                                                        {row.errors.length > 0 && <em>{row.errors.length} error{row.errors.length === 1 ? "" : "s"}</em>}
                                                    </button>
                                                    {expandedPreviewRows.has(row.row_number) && <div className={styles.previewCardBody}>
                                                        {row.errors.length > 0 && (
                                                            <ul className={styles.errorList}>
                                                                {row.errors.map((error) => <li key={error}>{error}</li>)}
                                                            </ul>
                                                        )}
                                                        <QuestionEditor
                                                            editorId={`preview-${row.row_number}-${rowIndex}`}
                                                            question={row.question}
                                                            referenceDrafts={previewReferenceDrafts[row.row_number]
                                                                ?? normalizeReferencesForEditor(row.question.question_references)}
                                                            onQuestionChange={(updates) => updatePreviewQuestion(rowIndex, updates)}
                                                            onReferencesChange={(references) => {
                                                                setPreviewReferenceDrafts((current) => ({
                                                                    ...current,
                                                                    [row.row_number]: references,
                                                                }));
                                                                updatePreviewQuestion(rowIndex, {
                                                                    question_references: serializeReferencesForSave(references),
                                                                });
                                                            }}
                                                            onOptionChange={(optionIndex, text) => updatePreviewOption(rowIndex, optionIndex, text)}
                                                            onAddOption={() => addPreviewOption(rowIndex)}
                                                            onRemoveOption={(optionIndex) => removePreviewOption(rowIndex, optionIndex)}
                                                            specialties={specialties}
                                                            fixedOptionCount={optionCount}
                                                        />
                                                        <button className={styles.removeQuestionButton} onClick={() => removePreviewRow(rowIndex)}>
                                                            Remove question from batch
                                                        </button>
                                                    </div>}
                                                </div>
                                            ))}
                                        </div>
                                        <div className={styles.footerActions}>
                                            <button className={styles.secondaryButton} onClick={() => {
                                                setPreview(null);
                                                setPreviewReferenceDrafts({});
                                            }} disabled={busy}>Choose another file</button>
                                            <button
                                                className={styles.primaryButton}
                                                onClick={submitPreview}
                                                disabled={busy || preview.rows.length === 0 || preview.rows.some((row) => row.errors.length > 0)}
                                            >
                                                {busy ? "Importing..." : `Import ${preview.rows.length} Questions`}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

interface QuestionEditorProps {
    editorId: string;
    question: EntryQuestion;
    referenceDrafts: QBankReferenceDraft[];
    onQuestionChange: (updates: Partial<EntryQuestion>) => void;
    onReferencesChange: (references: QBankReferenceDraft[]) => void;
    onOptionChange: (index: number, text: string) => void;
    onAddOption: () => void;
    onRemoveOption: (index: number) => void;
    specialties: string[];
    fixedOptionCount: number | null;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({
    editorId,
    question,
    referenceDrafts,
    onQuestionChange,
    onReferencesChange,
    onOptionChange,
    onAddOption,
    onRemoveOption,
    specialties,
    fixedOptionCount,
}) => (
    <div className={styles.questionEditor}>
        <label className={styles.fullWidth}>
            <span>Question text</span>
            <textarea
                value={question.question_text}
                onChange={(event) => onQuestionChange({ question_text: event.target.value })}
                rows={3}
            />
        </label>

        <div className={styles.optionsEditor}>
            <div className={styles.fieldHeader}>
                <span>Options</span>
                <small>Choose an answer only when supplying an explanation.</small>
            </div>
            {question.options.map((option, index) => (
                <div className={styles.optionRow} key={option.label}>
                    <input
                        type="radio"
                        name={`correct-answer-${editorId}`}
                        checked={question.correct_answer === option.label}
                        onChange={() => onQuestionChange({ correct_answer: option.label })}
                        aria-label={`Mark option ${option.label} correct`}
                    />
                    <span>{option.label}</span>
                    <input
                        type="text"
                        value={option.text}
                        onChange={(event) => onOptionChange(index, event.target.value)}
                        placeholder={`Option ${option.label}`}
                    />
                    <button
                        type="button"
                        onClick={() => onRemoveOption(index)}
                        disabled={fixedOptionCount !== null || question.options.length <= 2}
                        aria-label={`Remove option ${option.label}`}
                    >×</button>
                </div>
            ))}
            <div className={styles.optionActions}>
                <button type="button" onClick={onAddOption} disabled={fixedOptionCount !== null || question.options.length >= 6}>+ Add option</button>
                {question.correct_answer && (
                    <button type="button" onClick={() => onQuestionChange({ correct_answer: "" })}>Clear correct answer</button>
                )}
            </div>
        </div>

        <div className={styles.twoColumns}>
            <label>
                <span>Specialty</span>
                <select value={question.specialty} onChange={(event) => onQuestionChange({ specialty: event.target.value })}>
                    {specialties.map((specialty) => <option key={specialty} value={specialty}>{specialty}</option>)}
                </select>
            </label>
            <label>
                <span>Difficulty</span>
                <select value={question.difficulty} onChange={(event) => onQuestionChange({ difficulty: event.target.value })}>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                </select>
            </label>
        </div>

        <label className={styles.fullWidth}>
            <span>Explanation (optional with a correct answer)</span>
            <textarea
                value={question.explanation}
                onChange={(event) => onQuestionChange({ explanation: event.target.value })}
                rows={4}
                placeholder="Leave both answer and explanation blank to let Agent6 complete the question."
            />
        </label>

        <details className={styles.fullWidth}>
            <summary>Precomputed embedding (optional){question.embedding ? " — attached" : ""}</summary>
            <label className={styles.fullWidth}>
                <span>Embedding JSON</span>
                <textarea
                    value={typeof question.embedding === "string"
                        ? question.embedding
                        : question.embedding ? JSON.stringify(question.embedding, null, 2) : ""}
                    onChange={(event) => onQuestionChange({ embedding: event.target.value || null })}
                    rows={5}
                    placeholder="Paste the embedding object from your question preparation file"
                />
            </label>
            <p>Include model, dimensions, content_hash and vector. The server checks compatibility with the configured QBank model and the question content. After changing the specialty, stem or options, replace the embedding or remove it so it can be generated on publication.</p>
            {question.embedding && <button type="button" onClick={() => onQuestionChange({ embedding: null })}>Remove embedding</button>}
        </details>

        <div className={styles.referencesSection}>
            <div className={styles.fieldHeader}>
                <span>References</span>
                <small>Optional for completed questions.</small>
            </div>
            <QBankReferenceEditor references={referenceDrafts} onChange={onReferencesChange} />
        </div>
    </div>
);

export default QBankQuestionEntryModal;
